import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  CalendarDays,
  Loader2,
  RefreshCw,
  Sparkles,
  Target,
  Zap,
} from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMELINE = [
  {
    key: 'day_1_10',
    label: 'Days 1–10',
    subtitle: 'Diagnose & align',
    icon: Target,
    accent: '#0D9488',
  },
  {
    key: 'day_11_20',
    label: 'Days 11–20',
    subtitle: 'Intervene & coach',
    icon: CalendarDays,
    accent: '#0891B2',
  },
  {
    key: 'day_21_30',
    label: 'Days 21–30',
    subtitle: 'Embed & measure',
    icon: Zap,
    accent: '#6366F1',
  },
];

function resolveContext(analytics) {
  const depts = (analytics?.departments || []).filter((d) => !d.privacy_locked);
  const flagged = [...depts].sort(
    (a, b) => (b.high_risk_pct || 0) - (a.high_risk_pct || 0),
  )[0];
  const topDriver = analytics?.burnout_drivers?.[0];
  return {
    team_name: flagged?.name || 'Organization',
    top_driver: topDriver?.factor || 'Workplace friction',
    score: flagged?.high_risk_pct ?? topDriver?.impact ?? 0,
    flagged,
  };
}

export default function AIActionPlanner({ analytics, enabled = true }) {
  const ctx = resolveContext(analytics);
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchPlan = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/analytics/action-plan`,
        {
          team_name: ctx.team_name,
          top_driver: ctx.top_driver,
          score: ctx.score,
        },
        { withCredentials: true },
      );
      setPlan(data.plan);
      setMeta(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not generate an action plan.');
    } finally {
      setLoading(false);
    }
  }, [enabled, ctx.team_name, ctx.top_driver, ctx.score]);

  useEffect(() => {
    if (!enabled || !analytics?.has_data) return;
    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, analytics?.has_data, ctx.team_name, ctx.top_driver]);

  if (!enabled) return null;

  if (!analytics?.has_data) {
    return (
      <div
        className="glass-card rounded-3xl p-8 border border-slate-100 text-center"
        data-testid="ai-action-planner-empty"
      >
        <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
        <p className="font-heading text-lg font-medium text-slate-700">AI Action Planner</p>
        <p className="text-sm text-slate-500 mt-1">
          Complete at least one survey cycle to unlock a burnout mitigation strategy.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-testid="ai-action-planner"
    >
      {ctx.flagged && (ctx.flagged.risk_level === 'high' || ctx.flagged.risk_level === 'medium') && (
        <div
          className="rounded-2xl border p-4 flex items-start gap-3"
          style={{
            borderColor: ctx.flagged.risk_level === 'high' ? '#FECACA' : '#FDE68A',
            background: ctx.flagged.risk_level === 'high' ? '#FEF2F2' : '#FFFBEB',
          }}
          data-testid="flagged-department"
        >
          <AlertTriangle
            className={`w-5 h-5 mt-0.5 shrink-0 ${
              ctx.flagged.risk_level === 'high' ? 'text-red-500' : 'text-amber-500'
            }`}
          />
          <div>
            <p className="text-sm font-semibold text-slate-800">
              Flagged department: {ctx.flagged.name}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              {ctx.flagged.high_risk_pct}% high risk · Top driver: {ctx.top_driver} (
              {Math.round(ctx.score)}% signal)
            </p>
          </div>
        </div>
      )}

      <div className="relative overflow-hidden rounded-3xl border border-teal-100 bg-gradient-to-br from-slate-900 via-slate-900 to-teal-950 p-6 md:p-8 text-white shadow-lg shadow-teal-900/10">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-teal-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-indigo-500/15 blur-3xl" />

        <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h2 className="font-heading text-xl md:text-2xl font-semibold">
                AI Action Planner: Burnout Mitigation Strategy
              </h2>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-teal-500/20 text-teal-200 border border-teal-400/30 shadow-[0_0_12px_rgba(45,212,191,0.35)]">
                <Sparkles className="w-3 h-3" />
                AI-Generated
                {meta?.source === 'fallback' ? ' · Offline' : ''}
              </span>
            </div>
            <p className="text-sm text-slate-400">
              Tailored for <span className="text-teal-300 font-medium">{meta?.team_name || ctx.team_name}</span>
              {' · '}
              Focus: <span className="text-slate-200">{meta?.top_driver || ctx.top_driver}</span>
            </p>
          </div>
          <Button
            type="button"
            onClick={fetchPlan}
            disabled={loading}
            variant="outline"
            className="border-teal-500/40 bg-white/5 text-teal-100 hover:bg-teal-500/20 hover:text-white shrink-0"
            data-testid="regenerate-action-plan"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Regenerate Plan
          </Button>
        </div>

        {loading && !plan ? (
          <div className="relative flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
          </div>
        ) : error && !plan ? (
          <p className="relative text-sm text-red-300 py-8 text-center">{error}</p>
        ) : plan ? (
          <div className="relative space-y-5">
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 md:p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300 mb-1.5 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> Quick Win
              </p>
              <p className="text-sm text-emerald-50 leading-relaxed">{plan.quick_win}</p>
              {plan.estimated_impact && (
                <p className="text-xs text-emerald-200/80 mt-3 border-t border-emerald-400/20 pt-3">
                  Estimated impact: {plan.estimated_impact}
                </p>
              )}
            </div>

            <div className="relative pl-2">
              <div className="absolute left-[1.35rem] top-3 bottom-3 w-px bg-gradient-to-b from-teal-400/60 via-cyan-400/40 to-indigo-400/50" />
              <ol className="space-y-4">
                {TIMELINE.map(({ key, label, subtitle, icon: Icon, accent }) => (
                  <li key={key} className="relative flex gap-4">
                    <div
                      className="relative z-10 w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border border-white/10"
                      style={{ background: `${accent}33` }}
                    >
                      <Icon className="w-4.5 h-4.5" style={{ color: accent, width: 18, height: 18 }} />
                    </div>
                    <div className="flex-1 rounded-2xl bg-white/5 border border-white/10 p-4 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-2 mb-1.5">
                        <span className="text-sm font-semibold text-white">{label}</span>
                        <span className="text-xs text-slate-400">{subtitle}</span>
                      </div>
                      <p className="text-sm text-slate-300 leading-relaxed">{plan[key]}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}
